// src/data/worksheets/motivation.js — performance-side pair (authored).
// Built off the Motivation action plan: shrink the goal, reconnect the why, start small on flat days.

export const MOTIVATION_CHECKLIST = {
    id: 'motivation_checklist', topicId: 'motivation', kind: 'checklist',
    title: 'Motivation Checklist',
    intro: 'Motivation follows action more often than it leads it. Tick these off as you build them in.',
    sections: [
        { id: 'goal', title: 'Make the Goal Reachable', prompts: [
            { id: 'shrink', type: 'checklist', label: 'Shrink the Goal', items: [
                { id: 's1', label: 'Set one target you can hit this week, {not this season|not this quarter}.' },
                { id: 's2', label: 'Make it measurable — you should be able to say yes or no at the end of the week.' }
            ] },
            { id: 'ladder', type: 'checklist', label: 'Build the Ladder', items: [
                { id: 'l1', label: 'Write the big goal at the top and the next three rungs below it.' },
                { id: 'l2', label: 'Only look at the next rung when you train or work.' }
            ] }
        ] },
        { id: 'why', title: 'Reconnect the Why', prompts: [
            { id: 'reason', type: 'checklist', label: 'Your Reason', items: [
                { id: 'r1', label: 'Write one line on why this matters to you. Keep it where you\'ll see it.' },
                { id: 'r2', label: 'Read it before the sessions you least feel like doing.' }
            ] },
            { id: 'identity', type: 'checklist', label: 'Act Like the Person', items: [
                { id: 'i1', label: 'Decide what the person who reaches this goal does on a normal Tuesday — do that.' },
                { id: 'i2', label: 'Keep a short list of evidence that you are already that person.' }
            ] }
        ] },
        { id: 'flat', title: 'On Flat Days', prompts: [
            { id: 'start', type: 'checklist', label: 'Start Small', items: [
                { id: 'st1', label: 'Do five minutes. Starting is usually the hard part.' },
                { id: 'st2', label: 'Lower the bar, not the standard — a short session done beats a long one skipped.' }
            ] },
            { id: 'energy', type: 'checklist', label: 'Check the Tank', items: [
                { id: 'e1', label: 'Ask whether it is motivation or fatigue — sleep, food and stress fake a motivation problem.' },
                { id: 'e2', label: 'If it is fatigue, recover on purpose instead of forcing it.' }
            ] }
        ] },
        { id: 'momentum', title: 'Keep Momentum', prompts: [
            { id: 'wins', type: 'checklist', label: 'Notice Wins', items: [
                { id: 'w1', label: 'Write down one win at the end of each day, however small.' },
                { id: 'w2', label: 'Reward the process (showing up), not just the result.' }
            ] },
            { id: 'people', type: 'checklist', label: 'Borrow Energy', items: [
                { id: 'p1', label: '{Train or work alongside|Work alongside} someone on the days you expect to be flat.' },
                { id: 'p2', label: 'Tell one person your weekly target so it exists outside your head.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Did you hit the weekly target? Yes or no — then set the next one.' },
                { id: 'wk2', label: 'Note what made the flat days easier.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'You do not need to feel like it. You need a small enough next step and a reason you can see.'
};

export const MOTIVATION_WORKSHEET = {
    id: 'motivation_worksheet', topicId: 'motivation', kind: 'worksheet',
    title: 'Motivation Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'drive', type: 'scale', label: 'How motivated have you felt over the past week?', min: 1, max: 10, minLabel: 'Running on empty', maxLabel: 'Fired up' },
            { id: 'flatdays', type: 'list', label: 'When does it drop?', help: 'Situations, times of day, or types of session where motivation disappears.', count: 3 },
            { id: 'drain', type: 'text', label: 'Is it motivation, or something else?', help: 'Sleep, food, stress, boredom, a goal that feels too far away. Be honest.', multiline: true }
        ] },
        { id: 'understanding', title: 'Understanding Your Drive', prompts: [
            { id: 'why', type: 'text', label: 'Why does this matter to you?', help: 'One line. If it takes a paragraph, it is not the real reason yet.' },
            { id: 'past', type: 'text', label: 'A time you were highly motivated — what was different?', help: 'The goal, the people, the progress you could see.', multiline: true },
            { id: 'evidence', type: 'list', label: 'Evidence you are already the kind of person who does this', count: 3 }
        ] },
        { id: 'building', title: 'Building Momentum', prompts: [
            { id: 'biggoal', type: 'text', label: 'The big goal' },
            { id: 'rungs', type: 'list', label: 'The next three rungs', help: 'Each one reachable within a week or two.', count: 3, itemLabels: ['Rung 1', 'Rung 2', 'Rung 3'] },
            { id: 'fivemin', type: 'text', label: 'Your five-minute version', help: 'What you will do on a flat day when the full session is not happening.' },
            { id: 'rewards', type: 'list', label: 'Process rewards', help: 'Small rewards for showing up, not for results.', count: 2 }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'weektarget', type: 'text', label: 'This week\'s target', help: 'One thing you can say yes or no to on Sunday.' },
            { id: 'accountability', type: 'list', label: 'Who you will tell', count: 2 },
            { id: 'flatplan', type: 'text', label: 'Your flat-day plan', help: 'What you will do when you don\'t feel like it — decided now, not then.', multiline: true }
        ] },
        { id: 'track', title: 'Track It', description: 'Log the drive AND whether you started. The pattern that matters is how often action came before the feeling.', prompts: [
            { id: 'tracker', type: 'table', label: 'Daily motivation tracker', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'drive', label: 'Drive (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'effort', label: 'Effort put in (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'win', label: 'Win of the day', kind: 'text' },
                { id: 'notes', label: 'Notes', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'drive', label: 'Drive' }, { id: 'effort', label: 'Effort' }] } }
        ] }
    ],
    outro: 'Motivation is a result. Show up small, notice the win, and let the feeling catch up.'
};

export const MOTIVATION_WORKSHEETS = [MOTIVATION_CHECKLIST, MOTIVATION_WORKSHEET];
